import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import {
  PARTNER_BUSINESS_STATUS,
  toPartnerBusinessStatusFilter,
  type PartnerBusinessStatus,
} from '@/shared/utils/partnerBusinessStatus';
import {
  Pageable,
  PageResponse,
  type PresignedUrlRequest,
  type UploadResponse,
} from '@/entities/user/api/user.api';

/** @deprecated PARTNER_BUSINESS_STATUS 사용. 병의원·약국 공통 enum */
export const HOSPITAL_BUSINESS_STATUS_FILTER = PARTNER_BUSINESS_STATUS;
export type HospitalBusinessStatusFilter = PartnerBusinessStatus;
export const toHospitalBusinessStatusFilter = toPartnerBusinessStatusFilter;

/** 드롭다운 UI 전용. 목록·엑셀 쿼리에는 보내지 않는다 */
export const HOSPITAL_REGION_ALL = 'all';

/** GET /admin/hospitals/regions 원소. 래퍼 없는 배열 */
export interface HospitalRegionOption {
  sido: string;
  /** 세종처럼 하위 행정구역이 없으면 빈 배열 */
  sigungu: string[];
}

/** @AssertTrue 프로퍼티명. 시/도 누락·미지원 400의 errors[].field */
export const HOSPITAL_REGION_VALID_FIELD = 'regionValid';

// 병원 관련 타입 정의
export interface HospitalSearchRequest {
  hospitalSearchKey?: string;
  deptOfTreatmentSearchKey?: string;
  /** 미전송 = 전체. OPEN | SUSPENDED | CLOSED 만 허용 */
  businessStatusFilter?: PartnerBusinessStatus;
  /** 선택지의 sido 문자열. 생략·빈 값 = 전체 지역 */
  sido?: string;
  /** 해당 시/도의 sigungu 원소. sido 없이 보내면 400 */
  sigungu?: string;
}

export interface HospitalInfoResponse {
  clinicId: number;
  clinicName: string;
  clinicTypeCode: string;
  /** 요양기관기호 (레거시 hospitalInfo `ykiho`). 폐업 sentinel은 null */
  ykiho?: string | null;
  managerName: string;
  managerPhoneNumber: string;
  medicalDepartments: string[];
  /** 관리자 자유 입력 컬럼. 상태 판정에 쓰지 말 것 */
  businessStatus: string;
  hasStatusHistory: boolean;
  /** 폐업일(yyyy-MM-dd). null이면 영업 중 */
  closedOn?: string | null;
  /** 정상/휴업/폐업 판정값. 뱃지는 이 필드만 본다 */
  businessStatusType: PartnerBusinessStatus;
}

export interface HospitalDetailResponse {
  clinicId: number;
  clinicUniqueCode: string;
  /** 요양기관기호. 폐업 sentinel은 null */
  ykiho?: string | null;
  clinicName: string;
  clinicTypeCode: string;
  clinicPhoneNumber: string;
  clinicAddress: string;
  longitude: number;
  latitude: number;
  businessLicenseImage: string;
  businessLicenseNumber: string;
  representativeName: string;
  managerRole: string;
  managerName: string;
  managerPhoneNumber: string;
  managerEmail: string;
  medicalDepartments: string[];
  /** 관리자 자유 입력 컬럼. 상태 판정에 쓰지 말 것 */
  businessStatus: string;
  /** 폐업일(yyyy-MM-dd). null이면 영업 중 */
  closedOn?: string | null;
  /** 정상/휴업/폐업 판정값 */
  businessStatusType: PartnerBusinessStatus;
}

export interface HospitalUpdateRequest {
  clinicName?: string;
  clinicTypeCode?: string;
  clinicPhoneNumber?: string;
  clinicAddress?: string;
  businessLicenseImageUrl?: string;
  businessLicenseNumber?: string;
  representativeName?: string;
  managerRole?: string;
  managerName?: string;
  managerPhoneNumber?: string;
  managerEmail?: string;
  medicalDepartments?: string[];
  businessStatus?: string;
}

export interface HospitalBusinessLicenseCompleteRequest {
  clinicId: number;
  uploadKey: string;
}

/** GET /admin/hospitals/export 응답 */
export interface HospitalExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export const HOSPITAL_EXPORT_EMPTY_CODE = 'HOSPITAL_EXPORT_EMPTY';

/** 8만 건 동기 생성이라 기본 10초보다 길게 잡는다 */
export const HOSPITAL_EXPORT_TIMEOUT_MS = 900_000;

/**
 * 지역 파라미터로 보낼 값만 남긴다.
 * 공백·`all` 같은 UI 값은 생략과 같다. 서버는 빈 문자열을 생략으로 본다.
 */
export const toHospitalRegionParam = (
  value?: string | null,
): string | undefined => {
  if (value == null) return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed === HOSPITAL_REGION_ALL) return undefined;
  return trimmed;
};

/**
 * URL·드롭다운 값을 선택지와 맞춘다.
 * 선택지에 없는 시/도·시군구는 초기화한다. 시/도가 없으면 시군구도 버린다.
 * `regions`가 아직 없으면(로딩·실패) URL 값을 유지한다.
 */
export const sanitizeHospitalRegionFilter = (
  sido: string | undefined,
  sigungu: string | undefined,
  regions: HospitalRegionOption[] | undefined,
): { sido: string; sigungu: string } => {
  const nextSido = toHospitalRegionParam(sido) ?? '';
  const nextSigungu = toHospitalRegionParam(sigungu) ?? '';

  if (regions == null) {
    return { sido: nextSido, sigungu: nextSido ? nextSigungu : '' };
  }

  const matched = regions.find((region) => region.sido === nextSido);
  if (!matched) {
    return { sido: '', sigungu: '' };
  }

  if (!nextSigungu || !matched.sigungu.includes(nextSigungu)) {
    return { sido: matched.sido, sigungu: '' };
  }

  return { sido: matched.sido, sigungu: nextSigungu };
};

export const buildHospitalSearchParams = (
  searchRequest: HospitalSearchRequest,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (searchRequest.hospitalSearchKey) {
    params.hospitalSearchKey = searchRequest.hospitalSearchKey;
  }
  if (searchRequest.deptOfTreatmentSearchKey) {
    params.deptOfTreatmentSearchKey = searchRequest.deptOfTreatmentSearchKey;
  }
  const businessStatusFilter = toPartnerBusinessStatusFilter(
    searchRequest.businessStatusFilter,
  );
  if (businessStatusFilter) {
    params.businessStatusFilter = businessStatusFilter;
  }
  // 시군구만 보내면 400이므로 시/도가 있을 때만 같이 붙인다
  const sido = toHospitalRegionParam(searchRequest.sido);
  if (sido) {
    params.sido = sido;
    const sigungu = toHospitalRegionParam(searchRequest.sigungu);
    if (sigungu) {
      params.sigungu = sigungu;
    }
  }
  return params;
};

/** 조건에 맞는 병의원이 없으면 404 + HOSPITAL_EXPORT_EMPTY */
export const isHospitalExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === HOSPITAL_EXPORT_EMPTY_CODE;
};

type HospitalValidationErrorBody = {
  errors?: Array<{ field?: string }>;
};

/**
 * 지역 필터 400에서 되돌릴 드롭다운을 고른다.
 * 시/도 문제는 필드명이 sido가 아니라 regionValid 다.
 */
export const getHospitalRegionValidationField = (
  error: unknown,
): 'sido' | 'sigungu' | undefined => {
  if (!isAxiosError(error)) return undefined;
  const data = error.response?.data as HospitalValidationErrorBody | undefined;
  const fields = Array.isArray(data?.errors)
    ? data.errors.map((item) => item.field)
    : [];
  if (fields.includes('sigungu')) return 'sigungu';
  if (fields.includes(HOSPITAL_REGION_VALID_FIELD) || fields.includes('sido')) {
    return 'sido';
  }
  return undefined;
};

// 병원 서비스
export const hospitalService = {
  /**
   * 시/도별 시/군/구 선택지.
   * 응답은 래퍼 없는 배열. 서버가 5분 캐시하므로 화면 진입 시 한 번이면 충분하다.
   */
  getHospitalRegions: async (): Promise<HospitalRegionOption[]> => {
    const response = await apiClient.get<HospitalRegionOption[]>(
      createApiPath.hospitals.regions(),
    );
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * 병원 목록 조회.
   * 레거시와 동일하게 쿼리 파라미터를 flat하게 전달한다 (page는 0부터).
   */
  getHospitals: async (
    searchRequest: HospitalSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<HospitalInfoResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
      ...buildHospitalSearchParams(searchRequest),
    };

    const response = await apiClient.get(createApiPath.hospitals.list(), {
      params,
    });
    return response.data;
  },

  /**
   * 병의원 목록 엑셀.
   * 목록과 같은 필터로 서버가 파일을 만든 뒤 서명 URL만 돌려준다.
   */
  exportHospitals: async (
    searchRequest: HospitalSearchRequest,
  ): Promise<HospitalExportResponse> => {
    const response = await apiClient.get<HospitalExportResponse>(
      createApiPath.hospitals.export(),
      {
        params: buildHospitalSearchParams(searchRequest),
        timeout: HOSPITAL_EXPORT_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  // 병원 상세 조회
  getHospitalDetail: async (
    clinicId: number,
  ): Promise<HospitalDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.hospitals.detail(clinicId),
    );
    return response.data;
  },

  // 병원 정보 수정
  updateHospital: async (
    clinicId: number,
    updateData: HospitalUpdateRequest,
  ): Promise<void> => {
    await apiClient.put(createApiPath.hospitals.update(clinicId), updateData);
  },

  /** 병의원 사업자등록증 presigned URL 발급 */
  generateBusinessLicensePresignedUrl: async (
    request: PresignedUrlRequest,
  ): Promise<UploadResponse> => {
    const response = await apiClient.post(
      createApiPath.hospitals.businessLicensePresignedUrl(),
      request,
    );
    return response.data;
  },

  /** 병의원 사업자등록증 업로드 완료 — clinic.business_license_image 갱신 */
  completeBusinessLicenseUpload: async (
    request: HospitalBusinessLicenseCompleteRequest,
  ): Promise<void> => {
    await apiClient.post(
      createApiPath.hospitals.businessLicenseComplete(),
      request,
    );
  },
};
